import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiLayers } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { ErrorResponse } from "@core/types/errorResponse";
import InfoTooltip from "@core/components/InfoTooltip";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useReportSubGroupActions } from "@features/report/subgroup/hooks/useReportSubGroupActions";
import { ReportSubGroup } from "@features/report/subgroup/types/reportSubGroup";
import { UpdateReportSubGroupRequest } from "@features/report/subgroup/types/reportSubGroupRequests";

type UpdateSubGroupFormState = {
    report_sub_group_id: number;
    name: string;
    description?: string | null;
};

type FieldErrors = Partial<
    Record<keyof UpdateSubGroupFormState | keyof UpdateReportSubGroupRequest, string[]>
>;

const EMPTY_FORM: UpdateSubGroupFormState = {
    report_sub_group_id: 0,
    name: "",
    description: "",
};

const UpdateSubGroupModal: React.FC<{
    show: boolean;
    onHide: () => void;
    subGroup: ReportSubGroup | null;
    onUpdated?: () => void;
}> = ({ show, onHide, subGroup, onUpdated }) => {
    const resolvedNationId = Number(useScopeId());
    const [form, setForm] = useState<UpdateSubGroupFormState>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((state) => state.showValidation);
    const { busy, doSubGroupUpdate } = useReportSubGroupActions();

    useEffect(() => {
        if (subGroup) {
            setForm({
                report_sub_group_id: subGroup.report_sub_group_id,
                name: subGroup.name ?? "",
                description: subGroup.description ?? "",
            });
            return;
        }

        setForm(EMPTY_FORM);
    }, [subGroup]);

    const isUnchanged = (
        current: UpdateSubGroupFormState,
        original: ReportSubGroup | null | undefined
    ): boolean => {
        if (!original) return false;

        return (
            (current.name ?? "") === (original.name ?? "") &&
            (current.description ?? "") === (original.description ?? "")
        );
    };

    const handleClose = () => {
        setFormErrors({});
        onHide();
    };

    const handleChange = (field: keyof UpdateSubGroupFormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const handleSubmit = async () => {
        const messages: string[] = [];

        if (!subGroup) messages.push("No sub group selected.");
        if (!form.report_sub_group_id) messages.push("Sub group ID is missing.");
        if (!form.name?.trim()) messages.push("Sub group name is required.");
        if (subGroup && isUnchanged(form, subGroup)) messages.push("No changes to save.");

        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        try {
            setFormErrors({});

            const payload: UpdateReportSubGroupRequest = {
                name: form.name,
                description: form.description,
            };

            const response = await doSubGroupUpdate(
                resolvedNationId,
                form.report_sub_group_id,
                payload
            );

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "Report sub group updated successfully.");
                handleClose();
                setForm(EMPTY_FORM);
                onUpdated?.();
                return;
            }

            const error = response.getError() as ErrorResponse;
            setFormErrors((error?.errors as FieldErrors) ?? {});
            showError(error);
        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="update-sub-group-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    <FiLayers className="me-2" aria-hidden />
                    Update Report Sub Group
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3 pb-4">
                <Form>
                    <Form.Group className="mb-3" controlId="updateSubGroupName">
                        <Form.Label>
                            Name
                            <span className="text-danger">*</span>
                            <InfoTooltip text="Name shown on template assignment and activity entry tabs." />
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter sub group name"
                            value={form.name ?? ""}
                            onChange={handleChange("name")}
                            isInvalid={!!formErrors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.name?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="updateSubGroupDescription">
                        <Form.Label>
                            Description
                            <InfoTooltip text="Optional notes to help admins understand the purpose of this subgroup." />
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Enter sub group description"
                            value={form.description ?? ""}
                            onChange={handleChange("description")}
                            isInvalid={!!formErrors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {formErrors.description?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={handleSubmit}
                            disabled={busy}
                            aria-busy={busy}
                        >
                            {busy ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Updating Sub Group...
                                </>
                            ) : (
                                "Update Sub Group"
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default UpdateSubGroupModal;
